import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/* Aceternity logos-with-blur-flip, adapted for BookingFlows — rotating sets
   of the real infrastructure/integration logos with a blur-flip animation. */

const StripeMark = () => (
  <svg className="h-7 w-auto" viewBox="0 0 60 25" fill="none" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
  </svg>
);

const NamedMark = ({ src, label, imgClass = "h-8 w-8" }) => (
  <span className="flex items-center gap-2.5">
    <img src={src} alt={label} className={imgClass} loading="lazy" />
    <span className="text-base font-semibold text-gray-700">{label}</span>
  </span>
);

const LOGO_SETS = [
  [
    { name: "Stripe", node: <StripeMark /> },
    { name: "Ziina", node: <img src="/img/ziina-lockup.png" alt="Ziina — زينة" className="h-10 w-auto" loading="lazy" data-testid="trusted-infra-ziina" /> },
    { name: "QuickBooks", node: <img src="/images/integrations/quickbooks-logo.png" alt="Intuit QuickBooks" className="h-8 w-auto" loading="lazy" data-testid="trusted-infra-quickbooks" /> },
    { name: "AWS", node: <img src="/images/integrations/aws-logo.svg" alt="AWS" className="h-8 w-auto" loading="lazy" data-testid="trusted-infra-aws" /> },
    { name: "Google", node: <NamedMark src="/images/integrations/google-g.png" label="Google" /> },
  ],
  [
    { name: "WhatsApp", node: <img src="/images/integrations/whatsapp-lockup.webp" alt="WhatsApp" className="h-10 w-auto" loading="lazy" data-testid="trusted-infra-whatsapp" /> },
    { name: "Twilio", node: <img src="/images/integrations/twilio-lockup.webp" alt="Twilio" className="h-7 w-auto" loading="lazy" data-testid="trusted-infra-twilio" /> },
    { name: "Cloudflare", node: <img src="/images/integrations/cloudflare-logo.png" alt="Cloudflare" className="h-7 w-auto" loading="lazy" /> },
    { name: "Zapier", node: <img src="/images/integrations/zapier-lockup.webp" alt="Zapier" className="h-5 w-auto" loading="lazy" data-testid="trusted-infra-zapier" /> },
    { name: "Help Scout", node: <NamedMark src="https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/ynhf9qa7_android-chrome-512x512.png" label="Help Scout" /> },
  ],
  [
    { name: "Grafana", node: <img src="/images/integrations/grafana-labs-lockup.png" alt="Grafana Labs" className="h-6 w-auto" loading="lazy" data-testid="trusted-infra-grafana" /> },
    { name: "Sentry", node: <img src="/images/integrations/sentry-lockup.png" alt="Sentry" className="h-6 w-auto" loading="lazy" data-testid="trusted-infra-sentry" /> },
    { name: "Semgrep", node: <img src="/images/integrations/semgrep-lockup.webp" alt="Semgrep" className="h-[18px] w-auto" loading="lazy" data-testid="trusted-infra-semgrep" /> },
    { name: "Stormglass", node: <img src="/images/integrations/stormglass.png" alt="Stormglass" className="h-8 w-8 rounded-md" loading="lazy" data-testid="trusted-infra-stormglass" /> },
    { name: "Render", node: <img src="/images/integrations/render-lockup.png" alt="Render" className="h-[18px] w-auto" loading="lazy" /> },
  ],
];

export const LogosBlurFlip = ({ isAr }) => {
  const [logos, setLogos] = useState(LOGO_SETS);
  const [activeLogoSet, setActiveLogoSet] = useState(LOGO_SETS[0]);
  const [isAnimating, setIsAnimating] = useState(false);

  const flipLogos = () => {
    setLogos((current) => {
      const next = [...current.slice(1), current[0]];
      setActiveLogoSet(next[0]);
      setIsAnimating(true);
      return next;
    });
  };

  useEffect(() => {
    if (!isAnimating) {
      const timer = setTimeout(() => flipLogos(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]); // eslint-disable-line

  return (
    <section className="relative z-20 bg-white px-4 py-6 md:py-8" data-testid="trusted-infra-logos" dir={isAr ? "rtl" : undefined}>
      <p className="text-center text-[9px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.22em] text-gray-400 font-semibold px-2">
        {isAr ? "مدعوم ببنية تحتية من الطراز الأول" : "Powered by best-in-class infrastructure"}
      </p>
      <div className="relative mt-5 flex h-auto min-h-12 sm:h-12 w-full flex-wrap items-center justify-center gap-x-7 gap-y-4 sm:gap-x-9 md:gap-x-14">
        <AnimatePresence mode="popLayout" onExitComplete={() => setIsAnimating(false)}>
          {activeLogoSet.map((logo, idx) => (
            <motion.div
              initial={{ y: 30, opacity: 0, filter: "blur(8px)" }}
              animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
              exit={{ y: -30, opacity: 0, filter: "blur(8px)" }}
              transition={{ duration: 0.7, delay: 0.08 * idx, ease: [0.4, 0, 0.2, 1] }}
              key={logo.name + idx + "logo-flip"}
              className="relative flex items-center"
              title={logo.name}
            >
              {logo.node}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default LogosBlurFlip;
